<script lang="ts">
  import { cva, type VariantProps } from 'class-variance-authority';

  import Skeleton from './ui/skeleton.svelte';
  import ProductCard from './product-card/product-card.svelte';
  import SliderButton from './ui/slider-button.svelte';
  import CtaCard from './cta-card.svelte';

  import { cn } from '$lib/utils';

  import { t } from '$lib/i18n';

  import type { ProductCardFragment } from '$gql/storefront/operations';
  import type { CtaCardStoryblok } from '$types/bloks';

  export let products: ProductCardFragment[];
  export let cta: CtaCardStoryblok | null = null;
  export let loading = false;
  export let expectedResults = 3;
  export let scroll: HTMLDivElement | undefined = undefined;
  export let highlightPosition: 'left' | 'right' | null = 'left';
  export let removeMobileHighlight = false;
  export let variant: VariantProps<typeof host>['variant'] = 'desktop-carousel';
  let className: string | undefined = undefined;
  export { className as class };

  const host = cva([], {
    variants: {
      variant: {
        grid: 'block xl:hidden',
        'desktop-carousel':
          'lg:-mx-8 lg:flex lg:h-[480px] lg:gap-4 lg:overflow-x-auto lg:hide-scrollbar'
      }
    }
  });

  let screenSize = 0;
  $: scroll = undefined;
  const SCROLL_BY = 300;
  $: scrollLeftMax = scroll?.scrollWidth;
  $: scrollLeft = 0;
</script>

<svelte:window bind:innerWidth={screenSize} />
<!-- Slider Buttons-->
{#if variant === 'desktop-carousel'}
  <div class="lg:flex mb-4 justify-end w-full container mx-auto z-100 hidden">
    <div class="flex gap-2 px-2 lg:px-0 relative z-10">
      <SliderButton
        aria-label={$t('a11y.image-previous')}
        icon="chevron-left"
        variant="ghost"
        disabled={scrollLeft === 0}
        on:click={() => {
          scroll?.scrollBy({ left: -SCROLL_BY, behavior: 'smooth' });
        }}
      />
      <SliderButton
        aria-label={$t('a11y.image-next')}
        icon="chevron-right"
        variant="ghost"
        disabled={scrollLeftMax === scrollLeft}
        on:click={() => {
          scroll?.scrollBy({ left: SCROLL_BY, behavior: 'smooth' });
        }}
      />
    </div>
  </div>
{/if}
<!-- END Slider Buttons -->
<div
  bind:this={scroll}
  class={cn(host({ variant }), className)}
  on:scroll={() => {
    if (!scroll) return null;
    scrollLeftMax = scroll?.scrollWidth - screenSize;
    scrollLeft = scroll.scrollLeft;
  }}
>
  {#if products.length > 0 && !loading}
    {#each highlightPosition && !removeMobileHighlight ? [...products.slice(0, 1)] : [] as product ('product-grid-highlight' + product.id)}
      {#if product}
        <ProductCard
          {product}
          variant={highlightPosition ? 'highlight' : 'default'}
          class="col-span-2 lg:col-span-1 mb-2 lg:mb-0 lg:min-w-[332px] lg:ml-8 2xl:ml-[--x-margin]"
        />
      {/if}
    {/each}

    <div
      class="-mx-2 flex gap-2 overflow-x-auto pr-2 hide-scrollbar lg:mx-0 lg:ml-0 lg:gap-4 xl:overflow-x-visible lg:pr-0"
    >
      {#each highlightPosition && !removeMobileHighlight ? products.slice(1) : products as product, i ('product-grid-carousel' + product.id)}
        {#if product}
          <ProductCard
            {product}
            class={cn(
              'min-w-[45%] rounded-sm lg:min-w-[332px]',
              i === 0 && 'ml-2 lg:ml-0',
              i === 0 && !highlightPosition && 'lg:ml-8 2xl:ml-[--x-margin]',
              i === products.length - 2 && !cta && '2xl:mr-[--x-margin]',
              i === products.length - 1 && !cta && 'mr-4 lg:mr-8'
            )}
          />
        {/if}
      {/each}
      {#if cta && !loading}
        <CtaCard
          {cta}
          class={cn(
            'min-w-[40%] lg:w-full mr-1 lg:mr-8 2xl:mr-[--x-margin]',
            variant === 'desktop-carousel' && 'lg:min-w-[332px] lg:w-auto'
          )}
        />
      {/if}
    </div>
  {:else}
    <Skeleton
      class="aspect-[332/480] col-span-2 lg:col-span-1 mb-2 lg:mb-0 lg:ml-8 lg:min-w-[332px] rounded-md"
    />
    <div
      class="-mx-2 flex gap-2 overflow-x-auto pr-2 hide-scrollbar lg:mx-0 lg:ml-0 lg:gap-4 lg:overflow-x-visible lg:pr-0"
    >
      {#each [...Array(expectedResults)] as _}
        <Skeleton
          class={cn(
            'aspect-[183/304] lg:aspect-[332/480] mr-2 min-w-[40%] rounded-md first:ml-4 last:mr-4 lg:ml-0 lg:mr-0 lg:min-w-[332px] lg:first:ml-0 lg:last:mr-0'
          )}
        />
      {/each}
    </div>
  {/if}
</div>

{#if variant === 'grid'}
  <div
    class={cn('xl:grid xl:grid-cols-4 xl:gap-4', variant === 'grid' && 'hidden', $$restProps.class)}
  >
    {#each products || [] as product, i ('product-grid-as-grid' + product.id)}
      {#if product}
        {@const highlight =
          (highlightPosition === 'left' && i === 0) || (highlightPosition === 'right' && i === 2)}
        <ProductCard
          {product}
          variant={highlight ? 'highlight' : 'default'}
          class={cn(highlight ? 'col-span-2 lg:col-span-1' : 'col-span-1')}
        />
      {/if}
    {/each}

    {#if cta && !loading}
      <CtaCard {cta} />
    {/if}

    {#if loading}
      {#each [...Array(expectedResults)] as _, i}
        <Skeleton
          class={cn(
            'aspect-[183/304] lg:aspect-[332/480] h-full rounded-md',
            highlightPosition === 'left' && i === 0
              ? 'aspect-[332/480] col-span-2 lg:col-span-1'
              : highlightPosition === 'right' && i === 2
                ? 'aspect-[332/480] col-span-2 lg:col-span-1'
                : 'aspect-[183/304] lg:aspect-[332/480]'
          )}
        />
      {/each}
    {/if}
  </div>
{/if}
